// Language Analysis Panel Component
import { useState, useEffect } from "react";
import axios from "axios";
import { Languages, Loader2 } from "lucide-react";
import { translations } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { API, LANGUAGE_COLORS } from "./constants";

// Extended language colors for pie chart
const LANGUAGE_COLORS_DETAILED = {
  en: "#3B82F6",
  ko: "#8B5CF6",
  ja: "#EC4899",
  zh: "#EF4444",
  es: "#F59E0B",
  pt: "#10B981",
  th: "#06B6D4",
  vi: "#84CC16",
  id: "#F97316",
  ru: "#6366F1",
  ar: "#14B8A6",
  hi: "#A855F7",
  mixed: "#6B7280",
  default: "#9CA3AF"
};

const LanguageAnalysisPanel = ({ post, lang = "en" }) => {
  const t = translations[lang] || translations.en;
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalysis = async () => {
    if (!post) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/comments/analyze-languages`, null, {
        params: { platform: post.platform, post_id: post.post_id, max_comments: 100 }
      });
      setAnalysis(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(t.languageAnalysis.commentsDisabled);
      } else {
        setError(err.response?.data?.detail || "Failed to analyze comments");
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for cached analysis on mount
  useEffect(() => {
    if (post) {
      axios.get(`${API}/posts/${post.post_id}/language-analysis`)
        .then(res => setAnalysis(res.data))
        .catch(() => {}); // Ignore 404
    }
  }, [post]);

  const chartData = analysis?.language_breakdown?.map(item => ({
    name: item.language,
    value: item.count,
    percentage: item.percentage,
    color: LANGUAGE_COLORS_DETAILED[item.language_code] || LANGUAGE_COLORS_DETAILED.default
  })) || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-cyan-400" />
          <h3 className="font-heading font-bold text-lg">{t.languageAnalysis.title}</h3>
          <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-xs">GPT-5.2</Badge>
        </div>
        <Button 
          onClick={fetchAnalysis} 
          disabled={loading}
          className="primary-glow"
          data-testid="analyze-languages-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Languages className="h-4 w-4 mr-2" />}
          {loading ? t.languageAnalysis.analyzing : t.languageAnalysis.analyzeBtn}
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {analysis && !error && (
        <div className="space-y-6">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{t.languageAnalysis.totalComments}: <strong className="text-foreground">{analysis.total_comments_analyzed}</strong></span>
            <span>{t.languageAnalysis.lastAnalyzed}: {new Date(analysis.analyzed_at).toLocaleString()}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="bg-muted/30 rounded-lg p-4">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                    formatter={(value, name) => [`${value} comments`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Language Breakdown Table */}
            <div className="space-y-3">
              {analysis.language_breakdown?.map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: LANGUAGE_COLORS[item.language_code] || LANGUAGE_COLORS.default }}
                      />
                      <span className="font-medium">{item.language}</span>
                      <Badge variant="outline" className="text-xs">{item.language_code.toUpperCase()}</Badge>
                    </div>
                    <div className="text-right">
                      <span className="font-mono">{item.count}</span>
                      <span className="text-muted-foreground ml-2">({item.percentage}%)</span>
                    </div>
                  </div>
                  <Progress value={item.percentage} className="h-2" />
                  {item.sample_comments?.length > 0 && (
                    <div className="pl-5 space-y-1 mt-2">
                      {item.sample_comments.slice(0, 2).map((comment, i) => (
                        <p key={i} className="text-xs text-muted-foreground truncate italic">&ldquo;{comment}&rdquo;</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!analysis && !error && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          <Languages className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{t.languageAnalysis.noData}</p>
        </div>
      )}
    </div>
  );
};

export default LanguageAnalysisPanel;
